import { type FC, useCallback, useContext } from "react";

import { Box, Flex } from "@mantine/core";
import { Colors, Control, Spinner, Switch } from "@snapchat/snap-design-system";
import { CheckIcon, Sizes } from "@snapchat/snap-design-system-icons";
import { clsx } from "clsx";

import { type ExclusiveLensStatus, type Visibility } from "../../gql-generated/types";
import { NewTag } from "../../shared/components";
import { SubmissionQuotaContext } from "../../shared/components/submission-quota/SubmissionQuotaContext";
import { SubmissionQuotaInline } from "../../shared/components/submission-quota/SubmissionQuotaInline";
import { type SubmissionQuota } from "../../shared/models/SubmissionQuota";
import { type LensPreviewMediaItem } from "../../shared/state";
import { isSubmissionQuotaExhaustedForEnforcement } from "../../shared/utils/submissionQuota";
import { validateExclusiveLens } from "../../submit/validation";
import { LENS_PLUS_APPLY_LINK_URL, LENS_PLUS_LEARN_MORE_LINK_URL } from "../constants";
import {
    getComponentVisibility,
    getExclusiveLensState,
    getUserEligibilityForLensPlus,
    shouldDisableToggle,
} from "../utils";

import { ExclusiveLensApplyButton } from "./ExclusiveLensApplyButton";
import styles from "./ExclusiveLensToggle.module.css";
import { ExclusiveLensValidationAlert } from "./ExclusiveLensValidationAlert";
import {
    ExclusiveLensHeader,
    ExclusiveLensSubheader,
    ExclusiveLensToggleTooltip,
    LearnMore,
    LensPlusPayoutsApplyDescription,
} from "./messages.generated";

import { UserInfoContext } from "src/app/shared/api/contexts";

interface ExclusiveLensToggleProps {
    hasMusic?: boolean;
    hasLyrics?: boolean;
    previewMediaItems: readonly LensPreviewMediaItem[];
    lens?: object; // undefined = new submission, defined = update
    exclusiveLensStatus: ExclusiveLensStatus;
    onExclusiveLensChange?: (isExclusive: boolean) => void;
    disabled?: boolean;
    acceptedLatestVersion?: boolean;
    acceptedOldVersion?: boolean;
    tosLoading?: boolean;
    triggerTosModal: () => void;
    canToggleExclusiveLens?: boolean;
    isExclusiveChecked?: boolean;
    visibility?: Visibility;
    lensCreatorPayoutEnrolled?: boolean;
    overrideContainerCss?: string;
    lensPlusSubmissionQuota?: SubmissionQuota | null;
}

export const ExclusiveLensToggle: FC<ExclusiveLensToggleProps> = ({
    hasMusic,
    hasLyrics,
    previewMediaItems,
    lens,
    exclusiveLensStatus,
    onExclusiveLensChange,
    disabled = false,
    acceptedLatestVersion = false,
    tosLoading = false,
    triggerTosModal,
    canToggleExclusiveLens = true,
    isExclusiveChecked,
    visibility,
    lensCreatorPayoutEnrolled = false,
    overrideContainerCss,
    lensPlusSubmissionQuota,
}) => {
    const userInfo = useContext(UserInfoContext);
    const userEligibilityForLensPlus = userInfo?.data?.getUserProfile.exclusiveLensProgramStatus;
    const validation = validateExclusiveLens({ hasMusic, hasLyrics, previewMediaItems, visibility });
    const { isExclusiveLens, isExclusiveLensPending } = getExclusiveLensState(exclusiveLensStatus);
    const { isUserEnrolledInLensPlus, isUserEligibleForLensPlus } = getUserEligibilityForLensPlus(
        userEligibilityForLensPlus,
        acceptedLatestVersion,
    );
    const { shouldRender, showValidationForUpdate } = getComponentVisibility(
        lens,
        isExclusiveLens,
        isExclusiveLensPending,
        validation.isValid,
        lensCreatorPayoutEnrolled,
    );
    const isLensPlusQuotaExhausted = !lens && isSubmissionQuotaExhaustedForEnforcement(lensPlusSubmissionQuota);
    const isExclusiveSelected = isExclusiveLensPending || Boolean(isExclusiveChecked);

    const handleChange = useCallback(
        (checked: boolean) => {
            if (checked && (isLensPlusQuotaExhausted || (!validation.isValid && isUserEligibleForLensPlus))) {
                return;
            }
            // The phone-verification gate lives in the parent section (MonetizationSection),
            // which can disable this toggle and Top Performer together while it checks.
            onExclusiveLensChange?.(checked);
        },
        [validation.isValid, onExclusiveLensChange, isUserEligibleForLensPlus, isLensPlusQuotaExhausted],
    );

    const containerStyles = clsx(
        styles.container,
        !isUserEligibleForLensPlus && styles.showApply,
        overrideContainerCss,
    );

    const controlWrapperStyle = clsx(styles.controlWrapperStyle, !isUserEligibleForLensPlus && styles.showApply);

    if (!shouldRender) {
        return null;
    }

    const isToggleDisabled =
        shouldDisableToggle(disabled, lens, isExclusiveLens, validation.isValid, canToggleExclusiveLens) ||
        (acceptedLatestVersion && isLensPlusQuotaExhausted && !isExclusiveSelected);

    return (
        <>
            {showValidationForUpdate && (
                <ExclusiveLensValidationAlert type="error" errorMessage={validation.errorMessage} />
            )}

            <Flex
                align={{ base: "flex-start", sm: "center" }}
                justify={{ base: "flex-start", sm: "space-between" }}
                id="exclusive-lens-container"
                className={containerStyles}
                direction={{ base: "column", sm: "row" }}
                gap={{ base: "xs", sm: "md" }}
            >
                <div className={controlWrapperStyle}>
                    <Control
                        className={styles.controlStyle}
                        label={
                            <div className="heading-h500">
                                <ExclusiveLensHeader />
                                {!lens && <NewTag className={styles.tag} />}
                            </div>
                        }
                        tooltip={<ExclusiveLensToggleTooltip />}
                        forId="toggle-exclusive-lens"
                    >
                        {isUserEnrolledInLensPlus ? (
                            <ExclusiveLensSubheader
                                learnMoreLink={
                                    <a href={LENS_PLUS_LEARN_MORE_LINK_URL} target="_blank" rel="noreferrer">
                                        <strong>
                                            <LearnMore />
                                        </strong>
                                    </a>
                                }
                            />
                        ) : (
                            <LensPlusPayoutsApplyDescription
                                learnMoreLink={
                                    <a href={LENS_PLUS_LEARN_MORE_LINK_URL} target="_blank" rel="noreferrer">
                                        <strong>
                                            <LearnMore />
                                        </strong>
                                    </a>
                                }
                            />
                        )}
                        {!lens && isUserEligibleForLensPlus && lensPlusSubmissionQuota ? (
                            <div className={styles.submissionQuota}>
                                <SubmissionQuotaInline
                                    quota={lensPlusSubmissionQuota}
                                    context={SubmissionQuotaContext.LensPlus}
                                />
                            </div>
                        ) : null}
                    </Control>
                </div>
                {!isUserEligibleForLensPlus && <ExclusiveLensApplyButton applyUrl={LENS_PLUS_APPLY_LINK_URL} />}
                {tosLoading ? (
                    <Spinner className={styles.loadingSpinner} />
                ) : isExclusiveLens ? (
                    <Flex bg={Colors.Green.V150} bdrs="100%" w={30} h={30} align="center" justify="center">
                        <CheckIcon fill={Colors.WHITE} size={Sizes.Large} />
                    </Flex>
                ) : isUserEnrolledInLensPlus || isUserEligibleForLensPlus ? (
                    <Switch
                        id="toggle-exclusive-lens"
                        isChecked={isExclusiveSelected}
                        isDisabled={isToggleDisabled}
                        onClick={acceptedLatestVersion ? handleChange : triggerTosModal}
                        data-testid="exclusive-lens-toggle"
                    />
                ) : null}
            </Flex>

            {!lens &&
                canToggleExclusiveLens &&
                !validation.isValid &&
                validation.errorMessage &&
                isUserEligibleForLensPlus && (
                    <Box mx="md">
                        <ExclusiveLensValidationAlert type="warning" errorMessage={validation.errorMessage} />
                    </Box>
                )}

            {!lens && isExclusiveLensPending && isUserEligibleForLensPlus && (
                <ExclusiveLensValidationAlert type="pending" />
            )}
        </>
    );
};
